// frontend/src/pages/SurveyDetails.js

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Sidebar from "../components/Sidebar";
import LoadingBar from "../components/LoadingBar";
import { useAuth } from "../contexts/AuthContext";
import { useSurvey } from "../contexts/SurveyContext";
import EditQuestion from "../components/EditQuestion";
import RemixQuestion from "../components/RemixQuestion";
import AddQuestion from "../components/AddQuestion";
import CommentsSection from "../components/CommentsSection";
import QuestionItem from "../components/QuestionItem";
import ShareSurveyModal from "../components/ShareSurveyModal";
import axiosInstance from "../axiosInstance";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast, ToastContainer } from "react-toastify";
import { sanitizeId } from "../utils/sanitizeId";
import "./SurveyDetails.css";
import { useSocket } from "../hooks/useSocket";
import { getSidebarNotificationsRef } from "../components/Sidebar";

// ----- Translation Core Languages -----
const CORE_LANGUAGES = [
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "zh", label: "Chinese (Simplified)" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

// ----- Helper: Process notifications -----
const processNotifications = (notifications, surveyId) => {
  if (!Array.isArray(notifications) || !surveyId) {
    return { highlightedQuestionIds: [], questionNotificationsMap: {} };
  }
  const highlightedQuestionIds = [];
  const questionNotificationsMap = {};
  notifications.forEach((notif) => {
    if (
      String(notif.surveyId) === String(surveyId) &&
      notif.type === "comment" &&
      !notif.isRead &&
      notif.questionId
    ) {
      const qId = String(notif.questionId);
      if (!highlightedQuestionIds.includes(qId)) {
        highlightedQuestionIds.push(qId);
      }
      if (!questionNotificationsMap[qId]) {
        questionNotificationsMap[qId] = [];
      }
      questionNotificationsMap[qId].push(notif._id);
    }
  });
  return { highlightedQuestionIds, questionNotificationsMap };
};

// ----- Helper: Retrieve translation for a given language -----
// Supports both plain objects and Maps for survey.translations.
const getTranslationForLang = (survey, lang) => {
  if (!survey || !survey.translations) return null;
  return typeof survey.translations.get === "function"
    ? survey.translations.get(lang)
    : survey.translations[lang] || null;
};

const SurveyDetails = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const { selectSurvey } = useSurvey();
  const { user } = useAuth();
  const socket = useSocket("http://localhost:8000", { transports: ["websocket"] });
  const userName = user?.name || "Anonymous";

  // Local state
  const [survey, setSurvey] = useState(null);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCommentsMap, setShowCommentsMap] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [highlightedQuestions, setHighlightedQuestions] = useState([]);
  const [questionNotificationsMap, setQuestionNotificationsMap] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isRemixing, setIsRemixing] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sortingSection, setSortingSection] = useState(null);
  const [addingQuestionIndex, setAddingQuestionIndex] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false); // Lock UI during translation
  const [translationProgress, setTranslationProgress] = useState(0); // Progress percentage
  const [translationStatus, setTranslationStatus] = useState(""); // Current status text

  // Dropdown for additional actions (Delete Survey, Export PDF)
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Translation-specific state
  const [translationDropdownOpen, setTranslationDropdownOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]); // selected language codes
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
  const languageOrder = useMemo(() => ["en", ...selectedLanguages], [selectedLanguages]);

  // Ref for auto-closing the translation dropdown
  const translationDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        translationDropdownRef.current &&
        !translationDropdownRef.current.contains(event.target)
      ) {
        setTranslationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prepopulate selectedLanguages from stored translations.
  useEffect(() => {
    if (survey && survey.translations) {
      let langs = [];
      if (typeof survey.translations === "object" && !survey.translations.get) {
        langs = Object.keys(survey.translations);
      } else if (typeof survey.translations.get === "function") {
        langs = Array.from(survey.translations.keys());
      }
      setSelectedLanguages(langs);
    }
  }, [survey]);

  // Clean title/description
  const cleanTitle = survey?.title ? survey.title.replace(/\*/g, "") : "Untitled Survey";
  const cleanDescription = survey?.description
    ? survey.description.replace(/\*/g, "")
    : "No description available.";

  // Determine displayed fields based on current language
  const currentLanguage = languageOrder[currentLanguageIndex] || "en";
  const currentTranslation = currentLanguage !== "en" ? getTranslationForLang(survey, currentLanguage) : null;
  const displayTitle =
    currentLanguage === "en"
      ? cleanTitle
      : currentTranslation && currentTranslation.title
      ? currentTranslation.title.replace(/\*/g, "")
      : cleanTitle;
  const displayDescription =
    currentLanguage === "en"
      ? cleanDescription
      : currentTranslation && currentTranslation.description
      ? currentTranslation.description.replace(/\*/g, "")
      : cleanDescription;

  // Re-fetch survey
  const reFetchSurvey = useCallback(async () => {
    try {
      const data = await selectSurvey(id);
      if (data?.survey) {
        setSurvey(data.survey);
      }
    } catch (err) {
      console.error("Error re-fetching survey:", err);
    }
  }, [id, selectSurvey]);

  // Fetch survey details and notifications
  const fetchSurveyDetailsAndNotifications = useCallback(async () => {
    try {
      let surveyData;
      if (token) {
        const sharedUrl = `/surveys/shared/${token}`;
        const resp = await axiosInstance.get(sharedUrl);
        surveyData = resp.data.survey;
      } else {
        const resp = await selectSurvey(id);
        surveyData = resp.survey;
      }
      if (!surveyData) {
        setError("Survey not found.");
        return;
      }
      // Fetch comments
      let commentsResp;
      if (token) {
        const sharedCommentsUrl = `/surveys/shared/${token}/comments`;
        commentsResp = await axiosInstance.get(sharedCommentsUrl);
      } else {
        const normalCommentsUrl = `/surveys/${surveyData._id}/comments`;
        commentsResp = await axiosInstance.get(normalCommentsUrl);
      }
      const processedComments = {};
      Object.entries(commentsResp.data.comments || {}).forEach(([questionId, list]) => {
        processedComments[questionId] = list.map((c) => ({
          ...c,
          isRead: typeof c.isRead === "boolean" ? c.isRead : false,
        }));
      });
      // Fetch notifications
      const notificationsUrl = "/notifications";
      const notificationsResp = await axiosInstance.get(notificationsUrl);
      const { highlightedQuestionIds, questionNotificationsMap } =
        processNotifications(notificationsResp.data, String(surveyData._id));
      setSurvey(surveyData);
      setComments(processedComments);
      setHighlightedQuestions(highlightedQuestionIds);
      setQuestionNotificationsMap(questionNotificationsMap);
      setError(null);
    } catch (err) {
      console.error("Error fetching survey details:", err);
      if (err.response?.status === 404) {
        setError("Survey not found (404). Please check the URL or token.");
      } else {
        setError("Failed to load the survey.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, token, selectSurvey]);

  useEffect(() => {
    fetchSurveyDetailsAndNotifications();
  }, [fetchSurveyDetailsAndNotifications]);

  useEffect(() => {
    if (survey && Array.isArray(survey.sections)) {
      const initShowComments = {};
      survey.sections.forEach((section) => {
        section.questions.forEach((q) => {
          initShowComments[q._id] = false;
        });
      });
      setShowCommentsMap(initShowComments);
    }
  }, [survey]);

  useEffect(() => {
    if (!socket) return;
    const handleNewComment = (data) => {
      if (String(data.surveyId) === String(id)) {
        setComments((prev) => {
          const copy = { ...prev };
          if (!copy[data.questionId]) copy[data.questionId] = [];
          copy[data.questionId].push({
            surveyId: data.surveyId,
            questionId: data.questionId,
            commenterName: data.commenterName,
            commentText: data.commentText,
            createdAt: new Date(data.createdAt).toISOString(),
            isRead: false,
          });
          return copy;
        });
      }
    };
    socket.on("newComment", handleNewComment);
    return () => socket.off("newComment", handleNewComment);
  }, [socket, id]);

  const toggleGlobalComments = useCallback(() => {
    if (!survey || !Array.isArray(survey.sections)) return;
    const newVal = !showComments;
    const newMap = {};
    survey.sections.forEach((section) => {
      section.questions.forEach((q) => {
        newMap[q._id] = (comments[q._id]?.length > 0) && newVal;
      });
    });
    setShowCommentsMap(newMap);
    setShowComments(newVal);
  }, [survey, comments, showComments]);

  const toggleQuestionComments = useCallback((questionId) => {
    setShowCommentsMap((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  }, []);

  const markNotificationsAsRead = useCallback(
    async (questionId) => {
      const notificationIds = questionNotificationsMap[questionId];
      if (!notificationIds || notificationIds.length === 0) return;
      try {
        await axiosInstance.put("/notifications/bulk/read", { notificationIds });
        setComments((prev) => {
          const updated = { ...prev };
          if (updated[questionId]) {
            updated[questionId] = updated[questionId].map((c) => ({ ...c, isRead: true }));
          }
          return updated;
        });
        setHighlightedQuestions((prev) => prev.filter((qid) => qid !== questionId));
        setQuestionNotificationsMap((prev) => {
          const copy = { ...prev };
          delete copy[questionId];
          return copy;
        });
        const sidebarRef = getSidebarNotificationsRef();
        if (typeof sidebarRef === "function") sidebarRef();
        toast.success("Comments marked as read!");
      } catch (err) {
        console.error("Error marking notifications as read:", err);
        toast.error("Failed to mark comments as read.");
      }
    },
    [questionNotificationsMap]
  );

  const handleAddComment = useCallback(
    async (questionId, commentText) => {
      if (!questionId || !commentText.trim()) {
        toast.error("Invalid question ID or empty comment.");
        return;
      }
      try {
        const url = token
          ? `/surveys/shared/${token}/comments`
          : `/surveys/${survey?._id || id}/comments`;
        const resp = await axiosInstance.post(url, {
          questionId,
          commentText: commentText.trim(),
          commenterName: userName,
        });
        const newComment = resp.data.comment;
        setComments((prev) => {
          const copy = { ...prev };
          copy[questionId] = [...(copy[questionId] || []), newComment];
          return copy;
        });
        toast.success("Comment added successfully!");
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to add comment. Please try again.");
      }
    },
    [token, id, userName]
  );

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index)
        return;
      if (!survey || !Array.isArray(survey.sections)) return;
      
      const sourceSectionId = source.droppableId.replace(/^section-/, "");
      const destSectionId = destination.droppableId.replace(/^section-/, "");
      if (sourceSectionId !== destSectionId) {
        toast.error("Dragging between sections is not allowed.");
        return;
      }
      
      const section = survey.sections.find(
        (sec) => sanitizeId(sec._id) === sanitizeId(sourceSectionId)
      );
      if (!section) return;
      
      const reordered = Array.from(section.questions);
      const [movedQuestion] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, movedQuestion);
      
      const updatedSections = survey.sections.map((sec) =>
        sanitizeId(sec._id) === sanitizeId(section._id)
          ? { ...sec, questions: reordered }
          : sec
      );
      
      // Update local state immediately
      setSurvey({ ...survey, sections: updatedSections });
      
      // Persist the new order to the backend
      try {
        await axiosInstance.put(`/surveys/${survey._id}`, { sections: updatedSections });
        toast.success("Questions reordered and saved successfully!");
        await reFetchSurvey();
        setSortingSection(null);
      } catch (error) {
        console.error("Error saving question order:", error);
        toast.error("Failed to save question order. Please try again.");
      }
    },
    [survey, reFetchSurvey]
  );

  // Updated handleTranslate function with discrete task simulation
  const handleTranslate = useCallback(
    async (force = false) => {
      if (selectedLanguages.length === 0) return;
      setIsTranslating(true);
      setTranslationProgress(0);
      setTranslationStatus("Starting translation...");
      
      // Define discrete tasks with target progress values and delays
      const tasks = [
        { label: "Translating questions", progress: 20, delay: 4000 },
        { label: "Translating answers", progress: 40, delay: 4000 },
        { label: "Ensuring correct context", progress: 60, delay: 4000 },
        { label: "Completing survey", progress: 80, delay: 4000 },
        { label: "Saving translations", progress: 90, delay: 4000 },
      ];
      
      let currentTask = 0;
      const simulateTask = () => {
        if (currentTask < tasks.length) {
          const task = tasks[currentTask];
          setTranslationStatus(task.label);
          setTranslationProgress(task.progress);
          currentTask++;
          setTimeout(simulateTask, task.delay);
        }
      };
      
      simulateTask();
      
      try {
        await axiosInstance.post(`/surveys/${id}/translate`, {
          targetLanguages: selectedLanguages,
          force,
        });
        setTranslationDropdownOpen(false);
        setCurrentLanguageIndex(0);
        toast.success("Survey translated successfully!");
        // Finalize progress
        setTranslationStatus("Finalizing...");
        setTranslationProgress(100);
        await reFetchSurvey();
      } catch (err) {
        console.error("Error translating survey:", err);
        toast.error("Failed to translate survey.");
      } finally {
        // Wait a moment to show 100% before unlocking the UI
        setTimeout(() => {
          setIsTranslating(false);
          setTranslationProgress(0);
          setTranslationStatus("");
        }, 1000);
      }
    },
    [id, selectedLanguages, reFetchSurvey]
  );

  const handleShareSurvey = useCallback(() => setIsShareModalOpen(true), []);
  const handleCloseShareModal = useCallback(() => setIsShareModalOpen(false), []);
  const handleEditQuestion = useCallback((question) => {
    setCurrentQuestion(question);
    setIsEditing(true);
  }, []);
  const handleRemixQuestion = useCallback((question) => {
    if (!question) return;
    setCurrentQuestion(question);
    setIsRemixing(true);
  }, []);
  const handleDeleteQuestion = useCallback((questionId) => {
    setDeleteQuestionId(questionId);
  }, []);

  const confirmDeleteQuestion = useCallback(async () => {
    if (!survey?._id || !deleteQuestionId) {
      toast.error("Invalid survey or question ID.");
      return;
    }
    const url = token
      ? `/surveys/shared/${token}/questions/${deleteQuestionId}`
      : `/surveys/${survey._id}/questions/${deleteQuestionId}`;
    try {
      await axiosInstance.delete(url);
      await reFetchSurvey();
      if (selectedLanguages.length > 0) {
        await handleTranslate(true);
      }
      setDeleteQuestionId(null);
      toast.success("Question deleted successfully.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete question.");
    }
  }, [deleteQuestionId, survey, token, reFetchSurvey, handleTranslate, selectedLanguages]);

  const handleDeleteSurvey = useCallback(async () => {
    try {
      await axiosInstance.delete(`/surveys/${survey._id}`);
      toast.success("Survey deleted successfully.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete survey.");
    }
  }, [survey, navigate]);

  const handleExportSurvey = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/surveys/${survey._id}/export`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${cleanTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to export survey.");
    }
  }, [survey, cleanTitle]);

  const handlePreviousLanguage = () => {
    setCurrentLanguageIndex((prev) =>
      prev === 0 ? languageOrder.length - 1 : prev - 1
    );
  };
  const handleNextLanguage = () => {
    setCurrentLanguageIndex((prev) =>
      prev === languageOrder.length - 1 ? 0 : prev + 1
    );
  };

  const helmetTitle = `${cleanTitle} | survai`;
  const helmetDescription = cleanDescription;

  if (loading) return <LoadingBar />;
  if (error) return <p className="error-message">{error}</p>;
  if (!survey) return <p>No survey details found.</p>;
  if (!Array.isArray(survey.sections)) {
    return <p>Survey has no sections array. Invalid data or server error.</p>;
  }

  return (
    <div className="survey-details-page">
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDescription} />
      </Helmet>
      <div className="app-container">
        <Sidebar
          userName={userName}
          onLogout={() => {}}
          onPurchaseTokens={() => {}}
          isDarkMode={false}
        />
        <div className="surveycontent">
          <ToastContainer />
          <div className="header-buttons">
            <button
              onClick={() => navigate("/dashboard")}
              className="back-button"
              aria-label="Back to Dashboard"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            <div className="action-buttons">
              {/* Translation Button & Dropdown */}
              <div className="translation-dropdown-container" ref={translationDropdownRef}>
                <button
                  className="translation-toggle-button"
                  onClick={() => setTranslationDropdownOpen(!translationDropdownOpen)}
                  aria-label="Select Translation Languages"
                >
                  <i className="fa-regular fa-language"></i>
                </button>
                {translationDropdownOpen && (
                  <div className="translation-dropdown">
                    <div className="languages-grid">
                      {CORE_LANGUAGES.map((lang) => {
                        const isTranslated = !!getTranslationForLang(survey, lang.code);
                        return (
                          <label key={lang.code}>
                            <input
                              type="checkbox"
                              value={lang.code}
                              checked={selectedLanguages.includes(lang.code)}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (e.target.checked) {
                                  setSelectedLanguages([...selectedLanguages, value]);
                                } else {
                                  setSelectedLanguages(selectedLanguages.filter((l) => l !== value));
                                }
                              }}
                              disabled={isTranslated || isTranslating}
                            />
                            {lang.label}
                          </label>
                        );
                      })}
                    </div>
                    {selectedLanguages.some((lang) => getTranslationForLang(survey, lang)) ? (
                      <button 
                        onClick={() => handleTranslate(true)}
                        disabled={isTranslating}
                      >
                        {isTranslating ? "Translating survey, please wait..." : "Retranslate Survey"}
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleTranslate(false)}
                        disabled={isTranslating}
                      >
                        {isTranslating ? "Translating survey, please wait..." : "Translate Survey"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* Share Button */}
              <button onClick={handleShareSurvey} className="share-button" aria-label="Share Survey">
                <i className="fa-solid fa-arrow-up-from-bracket"></i> Share
              </button>
              {/* Additional Actions Dropdown */}
              <div className="dropdown">
                <button
                  className="dropdown-button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                  aria-label="More Actions"
                >
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <button className="export-button" onClick={handleExportSurvey}>
                      <i className="fa-regular fa-file-export"></i>
                      <span>Export PDF</span>
                    </button>
                    <button className="delete-button" onClick={handleDeleteSurvey}>
                      <i className="fa-regular fa-delete-right"></i>
                      <span>Delete Survey</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Survey Title & Description */}
          <h1>{displayTitle}</h1>
          <p>{displayDescription}</p>
          {/* Language Navigation */}
          {languageOrder.length > 1 && (
            <div className="translation-nav">
              <button onClick={handlePreviousLanguage} aria-label="Previous language">
                <i className="fa-solid fa-arrow-left"></i>
              </button>
              <span className="language-label">
                {currentLanguage === "en"
                  ? "English"
                  : CORE_LANGUAGES.find((l) => l.code === currentLanguage)?.label ||
                    (currentLanguage ? currentLanguage.toUpperCase() : "Unknown")}
              </span>
              <button onClick={handleNextLanguage} aria-label="Next language">
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            {survey.sections.map((section, sectionIndex) => {
              const translatedSection =
                currentLanguage !== "en" &&
                getTranslationForLang(survey, currentLanguage) &&
                getTranslationForLang(survey, currentLanguage).sections
                  ? getTranslationForLang(survey, currentLanguage).sections[sectionIndex]
                  : null;
              const sectionTitle = translatedSection
                ? translatedSection.sectionTitle
                : section.sectionTitle || `Section ${sectionIndex + 1}`;
              const sectionSummary = translatedSection
                ? translatedSection.sectionSummary
                : section.sectionSummary || "No summary provided.";
              const isSorting = sortingSection === sectionIndex;
              return (
                <div key={section._id} className={`survey-section ${isSorting ? "sorting" : ""}`}>
                  <div className="section-header">
                    <div className="section-header-text">
                      <h2 className="section-title">{sectionTitle}</h2>
                      <p className="section-summary">{sectionSummary}</p>
                    </div>
                    <button
                      className={`sorting-button ${isSorting ? "pressed" : ""}`}
                      onClick={() => setSortingSection(sectionIndex)}
                      aria-label={
                        isSorting
                          ? `Disable sorting for ${sectionTitle}`
                          : `Enable sorting for ${sectionTitle}`
                      }
                      aria-pressed={isSorting}
                    >
                      <i className="fa-solid fa-sort"></i>
                    </button>
                  </div>
                  {isSorting ? (
                    <Droppable droppableId={`section-${sanitizeId(section._id)}`}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="question-list">
                          {section.questions.map((question, index) => {
                            if (!question?._id) return null;
                            const highlighted = highlightedQuestions.includes(String(question._id));
                            const questionComments = comments[question._id] || [];
                            const translatedQuestionText =
                              translatedSection &&
                              translatedSection.questions &&
                              translatedSection.questions[index]
                                ? translatedSection.questions[index].questionText
                                : question.questionText;
                            const translatedAnswerOptions =
                              translatedSection &&
                              translatedSection.questions &&
                              translatedSection.questions[index] &&
                              translatedSection.questions[index].answerOptions &&
                              translatedSection.questions[index].answerOptions.length > 0
                                ? translatedSection.questions[index].answerOptions
                                : question.answerOptions;
                            const modifiedQuestion = {
                              ...question,
                              questionText: translatedQuestionText,
                              answerOptions: translatedAnswerOptions,
                            };
                            return (
                              <Draggable
                                key={question._id}
                                draggableId={`question-${sanitizeId(question._id)}`}
                                index={index}
                              >
                                {(dragProvided, snapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    style={{
                                      ...dragProvided.draggableProps.style,
                                      cursor: isSorting ? (snapshot.isDragging ? "grabbing" : "grab") : "default",
                                    }}
                                    className={`draggable-question ${snapshot.isDragging ? "dragging" : ""}`}
                                  >
                                    <QuestionItem
                                      question={modifiedQuestion}
                                      index={index}
                                      highlighted={highlighted}
                                      isSortingMode={isSorting}
                                      showComments={!!showCommentsMap[question._id]}
                                      comments={questionComments}
                                      commentsLoading={false}
                                      handleAddComment={handleAddComment}
                                      markNotificationsAsRead={markNotificationsAsRead}
                                      handleEditQuestion={currentLanguage === "en" ? handleEditQuestion : null}
                                      handleRemixQuestion={currentLanguage === "en" ? handleRemixQuestion : null}
                                      handleDeleteQuestion={currentLanguage === "en" ? handleDeleteQuestion : null}
                                      userName={userName}
                                      onToggleComments={toggleQuestionComments}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    <div className="question-list">
                      {section.questions.map((question, index) => {
                        if (!question?._id) return null;
                        const highlighted = highlightedQuestions.includes(String(question._id));
                        const questionComments = comments[question._id] || [];
                        const translatedQuestionText =
                          translatedSection &&
                          translatedSection.questions &&
                          translatedSection.questions[index]
                            ? translatedSection.questions[index].questionText
                            : question.questionText;
                        const translatedAnswerOptions =
                          translatedSection &&
                          translatedSection.questions &&
                          translatedSection.questions[index] &&
                          translatedSection.questions[index].answerOptions &&
                          translatedSection.questions[index].answerOptions.length > 0
                            ? translatedSection.questions[index].answerOptions
                            : question.answerOptions;
                        const modifiedQuestion = {
                          ...question,
                          questionText: translatedQuestionText,
                          answerOptions: translatedAnswerOptions,
                        };
                        return (
                          <div key={question._id} className="static-question">
                            <QuestionItem
                              question={modifiedQuestion}
                              index={index}
                              highlighted={highlighted}
                              isSortingMode={false}
                              showComments={!!showCommentsMap[question._id]}
                              comments={questionComments}
                              commentsLoading={false}
                              handleAddComment={handleAddComment}
                              markNotificationsAsRead={markNotificationsAsRead}
                              handleEditQuestion={currentLanguage === "en" ? handleEditQuestion : null}
                              handleRemixQuestion={currentLanguage === "en" ? handleRemixQuestion : null}
                              handleDeleteQuestion={currentLanguage === "en" ? handleDeleteQuestion : null}
                              userName={userName}
                              onToggleComments={toggleQuestionComments}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button
                    className="add-question-button"
                    onClick={async () => {
                      await reFetchSurvey();
                      if (selectedLanguages.length > 0) {
                        await handleTranslate(true);
                      }
                      setAddingQuestionIndex(sectionIndex);
                    }}
                    aria-label={`Add question to ${sectionTitle}`}
                  >
                    <i className="fa-solid fa-plus"></i> Add new question
                  </button>
                  {addingQuestionIndex === sectionIndex && (
                    <AddQuestion
                      surveyId={survey._id}
                      sectionIndex={sectionIndex}
                      onUpdate={async (newQuestion) => {
                        await reFetchSurvey();
                        if (selectedLanguages.length > 0) {
                          await handleTranslate(true);
                        }
                        setAddingQuestionIndex(null);
                      }}
                      onClose={() => setAddingQuestionIndex(null)}
                    />
                  )}
                </div>
              );
            })}
          </DragDropContext>
          {isEditing && currentQuestion && (
            <EditQuestion
              surveyId={survey._id}
              question={currentQuestion}
              onClose={() => setIsEditing(false)}
              onUpdate={async (updatedQuestion) => {
                try {
                  await axiosInstance.put(
                    `/surveys/${survey._id}/questions/${updatedQuestion._id}`,
                    updatedQuestion
                  );
                  await reFetchSurvey();
                  if (selectedLanguages.length > 0) {
                    await handleTranslate(true);
                  }
                  setIsEditing(false);
                  toast.success("Question updated successfully!");
                } catch (error) {
                  console.error("Error updating question:", error);
                  toast.error("Failed to update question.");
                }
              }}
            />
          )}
          {isRemixing && currentQuestion && (
            <RemixQuestion
              surveyId={survey._id}
              questionId={currentQuestion._id}
              questionType={currentQuestion.questionType}
              onClose={() => setIsRemixing(false)}
              onUpdate={async (remixedQuestion) => {
                await reFetchSurvey();
                if (selectedLanguages.length > 0) {
                  await handleTranslate(true);
                }
                setIsRemixing(false);
                toast.success("Question remixed successfully!");
              }}
            />
          )}
          {deleteQuestionId && (
            <div className="delete-confirmation" role="dialog" aria-modal="true">
              <div className="delete-confirmation-content">
                <h2>Confirm Delete</h2>
                <p>Are you sure you want to delete this question?</p>
                <button onClick={confirmDeleteQuestion} className="confirm-delete">
                  Yes, Delete
                </button>
                <button onClick={() => setDeleteQuestionId(null)} className="cancel-delete">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {isShareModalOpen && (
            <ShareSurveyModal
              onClose={handleCloseShareModal}
              surveyId={survey._id}
              surveyTitle={cleanTitle}
            />
          )}
        </div>
      </div>
      {/* Full-screen overlay during translation with progress */}
      {isTranslating && (
        <div className="translation-overlay">
          <div className="translation-progress">
            {translationStatus}... {translationProgress}% completed
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${translationProgress}%` }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyDetails;
